import type {ReactNode} from 'react';
import {useCardTilt} from '../../../hooks/useCardTilt.ts';
import {PAYMENT_CARD_SIZE_CLASS} from '../CreditCard/cardDimensions.ts';
import {Chip, ContactlessMark} from './CardMarks.tsx';
import type {BankCardSkin} from './skins';
import type {BankCardBrandProps} from './skins/types.ts';

const CARD_SHADOW = '0 6px 14px -8px rgba(20,22,35,0.32), 0 1px 3px rgba(20,22,35,0.1)';

const MUTED_BRAND_COLOR = '#4B5563';

const HOVER_CARD_SHADOW = '0 14px 24px -12px rgba(20,22,35,0.38), 0 3px 6px rgba(20,22,35,0.12)';

interface Props {
    skin: BankCardSkin;
    brandProps: BankCardBrandProps;
    hideDetails: boolean;
    fadeTextOnHover: boolean;
    ariaLabel: string;
    isMuted: boolean;
    balanceLabel: ReactNode;
    balance: ReactNode;
    balanceSizeClass: string;
    holderName: string;
    accountLine: ReactNode;
    networkMark: ReactNode;
    overlay: ReactNode;
    footer: ReactNode;
}

export function CardFront({
    skin,
    brandProps,
    hideDetails,
    fadeTextOnHover,
    ariaLabel,
    isMuted,
    balanceLabel,
    balance,
    balanceSizeClass,
    holderName,
    accountLine,
    networkMark,
    overlay,
    footer,
}: Props) {
    const {Face, Brand} = skin;
    const detailsClass = hideDetails ? 'invisible' : '';
    const textClass = fadeTextOnHover
        ? `${detailsClass} transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0 group-has-[:focus-visible]:opacity-0`
        : detailsClass;
    const {isHovered, containerProps, cardStyle} = useCardTilt();

    return (
        <div className={`relative w-[340px] [perspective:1000px] ${isHovered ? 'z-20' : 'z-0'}`} {...containerProps}>
            <div
                tabIndex={0}
                role="group"
                aria-label={ariaLabel}
                className={`group relative rounded-[18px] outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-4 transition-transform duration-200 ease-out ${PAYMENT_CARD_SIZE_CLASS}`}
                style={cardStyle}
            >
                <div className="absolute inset-0 rounded-[18px] transition-shadow duration-200" style={{boxShadow: isHovered ? HOVER_CARD_SHADOW : CARD_SHADOW}} />
                <div className="absolute inset-0 overflow-hidden rounded-[18px]">
                    <Face />
                    <div className="pointer-events-none absolute inset-0 z-10 bg-[linear-gradient(118deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0)_34%,rgba(255,255,255,0)_66%,rgba(255,255,255,0.07)_100%)] mix-blend-screen" />

                    <div className={`absolute left-[8%] top-[9%] max-w-[84%] ${isMuted ? 'z-[25] grayscale' : ''}`} style={{color: isMuted ? MUTED_BRAND_COLOR : skin.contentColor}}>
                        <Brand {...brandProps} />
                    </div>

                    <Chip tone={skin.chipTone} className={`absolute left-[8.5%] top-[34%] ${detailsClass}`} />
                    <ContactlessMark color={skin.contactlessColor} className={`absolute left-[26.5%] top-[35.5%] ${detailsClass}`} />

                    <div className={`absolute right-[6%] top-[33%] text-right ${textClass}`}>
                        <p className="flex items-center justify-end gap-2 text-[10px] font-semibold uppercase tracking-[0.08em]" style={{color: skin.labelColor}}>
                            {balanceLabel}
                        </p>
                        <p className={`mt-1 whitespace-nowrap font-bold leading-none tracking-[-0.02em] tabular-nums [&_*]:font-bold ${balanceSizeClass}`} style={{color: skin.contentColor}}>
                            {balance}
                        </p>
                    </div>

                    <div className={`absolute bottom-[8%] left-[8%] max-w-[64%] ${textClass}`}>
                        <p className="truncate text-[11px] font-semibold uppercase tracking-[0.08em]" style={{color: skin.labelColor}} title={holderName}>
                            {holderName}
                        </p>
                        {accountLine !== '' && (
                            <p className="mt-1 truncate font-mono text-[13px] tracking-[0.06em] [&_*]:font-mono" style={{color: skin.contentColor}}>
                                {accountLine}
                            </p>
                        )}
                    </div>

                    <div className={`absolute bottom-[8%] right-[6%] flex w-[15%] items-end justify-end ${detailsClass}`}>
                        {networkMark}
                    </div>

                    {isMuted && (
                        <>
                            <div className="pointer-events-none absolute -inset-px z-20 bg-[#E4E5E8]" />
                            <div className="pointer-events-none absolute -inset-px z-20 bg-[repeating-linear-gradient(135deg,rgba(17,24,39,0.06)_0_2px,transparent_2px_10px)]" />
                            <span className="pointer-events-none absolute right-[6%] top-[11%] z-[26] -rotate-[8deg] rounded-md border-2 border-red-500/80 px-2 py-0.5 text-[12px] font-extrabold uppercase tracking-[0.18em] text-red-600/90">
                                Verlopen
                            </span>
                        </>
                    )}

                    {overlay}
                </div>
            </div>
            {footer}
        </div>
    );
}
